import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { buildPublicFileUrl } from "../config/api";
import "./About.css";

const toAssetUrl = (path) => {
  if (!path) return "";
  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) return path;
  if (path.startsWith("/uploads") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }
  return path;
};

const VISUALS = {
  hero:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0014-golden-river-boat-silhouette-sunset-photography/golden-river-boat-silhouette-sunset-photography-mayachitro.webp",

  sunsetBird:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0008-flying-bird-over-golden-river-sunset-photography/flying-bird-over-golden-river-sunset-photography-mayachitro.webp",

  mistyForest:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0011-misty-forest-river-landscape-nature-photography/misty-forest-river-landscape-nature-photography-mayachitro.webp",

  leaf:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0010-half-colored-leaf-closeup-black-green-tones-nature-pattern/half-colored-leaf-closeup-black-green-tones-nature-pattern-mayachitro.webp",

  flower:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0005-bougainvillea-bloom-urban-architecture/preview.webp",

  spider:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0007-tiny-jumping-spider-green-leaf-macro-photography/tiny-jumping-spider-green-leaf-macro-photography-mayachitro.webp",

  dewLeaf:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0006-dew-drops-green-leaf-macro-photography/dew-drops-green-leaf-macro-photography-mayachitro.webp",

  leftBird:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0012-left-flying-bird-over-golden-river-sunset-photography/left-flying-bird-over-golden-river-sunset-photography-mayachitro.webp",

  fisherman:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0013-fisher-pulling-net-quiet-river-shore-photography/fisher-pulling-net-quiet-river-shore-photography-mayachitro.webp",

  solitaryBoat:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0015-solitary-boat-golden-river-sunset-photography/solitary-boat-golden-river-sunset-photography-mayachitro.webp",

  treeLineSunset:
    "/uploads/public/products/mayachitro/photo-downloads/MC-0016-golden-river-tree-line-sunset-photography/golden-river-tree-line-sunset-photography-mayachitro.webp",
};

function SmartImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);

  if (failed || !src) {
    return (
      <div className={`about-image-fallback ${className}`}>
        <span>MayaChitro</span>
      </div>
    );
  }

  return (
    <img
      src={toAssetUrl(src)}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function About() {
  useEffect(() => {
    document.title = "About MayaChitro | Photography, Wall Art & Licensing";
  }, []);

  return (
    <main className="about-page">
      {/* HERO */}
      <section className="about-hero">
        <div className="about-hero-copy">
          <p className="about-kicker">About MayaChitro</p>

          <h1>Original photography and wall art rooted in Bangladesh.</h1>

          <p>
            MayaChitro is a curated visual marketplace for meaningful photo
            downloads, printable wall art, and licensing-ready images created
            for homes, storytellers, publishers, brands, and creative buyers.
          </p>

          <div className="about-actions">
            <Link to="/shop?brand=mayachitro" className="about-btn primary">
              Shop Photography
            </Link>

            <Link to="/collections" className="about-btn ghost">
              Browse Collections
            </Link>

            <Link to="/licensing" className="about-btn soft">
              Licensing
            </Link>
          </div>
        </div>

        <div className="about-hero-visual">
          <SmartImage
            src={VISUALS.hero}
            alt="Golden river boat sunset photograph by MayaChitro"
            className="about-main-photo"
          />

          <div className="about-floating-note">
            <span>Captured & Curated</span>
            <strong>Visual stories for digital buyers worldwide.</strong>
          </div>
        </div>
      </section>

      {/* CREAM STORY SECTION */}
      <section className="about-story-light">
        <div className="about-story-image-grid">
          <SmartImage
            src={VISUALS.sunsetBird}
            alt="Golden sunset bird photo by MayaChitro"
          />
          <SmartImage
            src={VISUALS.mistyForest}
            alt="Misty forest river photograph by MayaChitro"
          />
          <SmartImage
            src={VISUALS.flower}
            alt="Bougainvillea flower photograph by MayaChitro"
          />
        </div>

        <div className="about-story-copy">
          <p className="about-kicker dark">The Story</p>

          <h2>More than a gallery. A visual marketplace with emotion and purpose.</h2>

          <p>
            MayaChitro presents original photographs and digital wall art with
            story, mood, and clear usage options. Each visual is selected to help
            buyers discover images that feel personal, cinematic, peaceful, and
            useful for real creative projects.
          </p>

          <p>
            The collection includes rivers, sunsets, rural life, street moments,
            portraits, flowers, macro details, cloudy skies, and quiet natural
            scenes from Bangladesh and beyond.
          </p>

          <Link to="/stories" className="about-inline-link dark">
            Read Photo Stories →
          </Link>
        </div>
      </section>

      {/* FOUNDER + TRUST */}
      <section className="about-founder-section">
        <div className="about-founder-copy">
          <p className="about-kicker">Founder</p>

          <h2>Created and curated by Ariful Islam Shohag.</h2>

          <p>
            MayaChitro is built by Ariful Islam Shohag, a visual storyteller and
            photographer based in Bangladesh. The goal is to turn original
            photography into a trusted digital marketplace where buyers can
            collect, print, download, and license meaningful visuals.
          </p>
        </div>

        <div className="about-trust-grid">
          <article>
            <span>01</span>
            <strong>Original Work</strong>
            <p>Photos captured and curated under the MayaChitro brand.</p>
          </article>

          <article>
            <span>02</span>
            <strong>Digital Access</strong>
            <p>Created for instant downloads, wall art, and image licensing.</p>
          </article>

          <article>
            <span>03</span>
            <strong>Buyer Focused</strong>
            <p>Built for homes, brands, publishers, designers, and creators.</p>
          </article>
        </div>
      </section>

      {/* PHOTO-FIRST OFFER SECTION */}
      <section className="about-offer-section">
        <div className="about-section-heading">
          <p className="about-kicker">What You Can Find</p>
          <h2>Photography, wall art, and licensing in one place.</h2>
        </div>

        <div className="about-offer-grid">
          <article>
            <SmartImage
              src={VISUALS.hero}
              alt="Photo downloads by MayaChitro"
            />
            <div>
              <span>Photo Downloads</span>
              <h3>High-resolution visuals</h3>
              <p>
                Original images for personal use, blogs, editorial stories,
                creative projects, and wall art.
              </p>
            </div>
          </article>

          <article>
            <SmartImage
              src={VISUALS.leaf}
              alt="Printable wall art by MayaChitro"
            />
            <div>
              <span>Wall Art Ready</span>
              <h3>Decor-friendly images</h3>
              <p>
                Calm, cinematic, and interior-friendly visuals for homes,
                offices, gallery walls, and styled spaces.
              </p>
            </div>
          </article>

          <article>
            <SmartImage
              src={VISUALS.spider}
              alt="Commercial licensing ready macro photograph"
            />
            <div>
              <span>Licensing</span>
              <h3>Clear usage options</h3>
              <p>
                Personal, editorial, commercial, and extended commercial
                options can help buyers choose the right use.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* DARK / LIGHT BRAND SPLIT */}
      <section className="about-brand-split">
        <div className="about-brand-card dark-card">
          <p className="about-kicker">MayaChitro</p>
          <h2>Photography with story, light, and emotion.</h2>
          <p>
            MayaChitro focuses on original photography, cultural memory, natural
            light, river life, portraits, macro details, and quiet visual
            storytelling.
          </p>
          <Link to="/shop?brand=mayachitro">Shop MayaChitro Photos →</Link>
        </div>

        <div className="about-brand-card cream-card" hidden>
          <p className="about-kicker dark">Cloudy Pixel Studio</p>
          <h2>Printable wall art for modern interiors.</h2>
          <p>
            Cloudy Pixel Studio adds decorative posters, printable wall art,
            digital designs, and interior-friendly visuals under the same
            marketplace experience.
          </p>
          <Link to="/shop?brand=cloudy-pixel-studio">
            Explore Wall Art →
          </Link>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="about-gallery-strip">
        <div className="about-section-heading slim">
          <p className="about-kicker">Visual Direction</p>
          <h2>Calm. Cinematic. Collectible.</h2>
        </div>

        <div className="about-mini-gallery">
  <SmartImage src={VISUALS.sunsetBird} alt="Flying bird over golden river sunset photograph" />
  <SmartImage src={VISUALS.mistyForest} alt="Misty forest river landscape photograph" />
  <SmartImage src={VISUALS.leaf} alt="Half colored leaf closeup abstract nature photograph" />
  <SmartImage src={VISUALS.flower} alt="Bougainvillea bloom urban architecture photograph" />
  <SmartImage src={VISUALS.spider} alt="Tiny jumping spider on green leaf macro photograph" />
  <SmartImage src={VISUALS.dewLeaf} alt="Dew drops on green leaf macro photograph" />
  <SmartImage src={VISUALS.leftBird} alt="Left flying bird over golden river sunset photograph" />
  <SmartImage src={VISUALS.fisherman} alt="Fisher pulling net by quiet river shore photograph" />
  <SmartImage src={VISUALS.solitaryBoat} alt="Solitary boat on golden river sunset photograph" />
  <SmartImage src={VISUALS.treeLineSunset} alt="Golden river tree line sunset photograph" />
</div>
      </section>

      {/* HOW IT WORKS */}
      <section className="about-process">
        <div className="about-section-heading">
          <p className="about-kicker">How It Works</p>
          <h2>A simple path from discovery to download.</h2>
        </div>

        <div className="about-process-grid">
          <article>
            <span>01</span>
            <strong>Discover</strong>
            <p>Browse photos, wall art, collections, and photo stories.</p>
          </article>

          <article>
            <span>02</span>
            <strong>Choose</strong>
            <p>Select a product and review download or license options.</p>
          </article>

          <article>
            <span>03</span>
            <strong>Buy</strong>
            <p>Complete checkout through the secure digital purchase flow.</p>
          </article>

          <article>
            <span>04</span>
            <strong>Use</strong>
            <p>Download your file and use it according to the selected license.</p>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="about-final-cta">
        <div>
          <p className="about-kicker">Start Exploring</p>
          <h2>Find a visual that feels meaningful.</h2>
          <p>
            Browse photography, printable wall art, and licensing-ready images
            created for homes, stories, brands, and creative projects.
          </p>
        </div>

        <div className="about-actions final">
          <Link to="/shop" className="about-btn primary">
            Browse Shop
          </Link>

          <Link to="/contact" className="about-btn ghost">
            Contact
          </Link>
        </div>
      </section>
    </main>
  );
}
